import { useEffect, useRef } from 'react';
import { makeStyles, mergeClasses, tokens } from '@fluentui/react-components';
import { useTranslation } from 'react-i18next';

const useStyles = makeStyles({
  eyebrow: {
    margin: 0,
    paddingTop: '32px',
    fontSize: '24px',
    lineHeight: '32px',
    fontWeight: 600,
    color: tokens.colorNeutralForeground3,
  },
  title: {
    margin: 0,
    marginTop: '10px',
    fontSize: '48px',
    lineHeight: '56px',
    fontWeight: 400,
    color: tokens.colorNeutralForeground1,
    outlineStyle: 'none',
  },
});

interface PageHeaderProps {
  /** The page's h1 text. Omit on pages that render their own h1 (eyebrow only). */
  title?: string;
  /** Move keyboard focus to the h1 on mount (route-transition focus management). */
  focusOnMount?: boolean;
  /** Re-fire the mount focus when this value changes (e.g. question ordinal). */
  focusKey?: string | number;
  /** Extra class merged onto the h1 (e.g. multi-line landing title metrics). */
  titleClassName?: string;
}

/**
 * Shared page header per Figma V4.6: the "AI-First Transformation Partner
 * Assessment" eyebrow (24/32/600, grey) and the page title slot (48/56/400).
 * Also keeps document.title in sync with the visible page title.
 */
export function PageHeader({ title, focusOnMount = false, focusKey, titleClassName }: PageHeaderProps): React.ReactElement {
  const styles = useStyles();
  const { t } = useTranslation('aita');
  const headingRef = useRef<HTMLHeadingElement>(null);
  const appTitle = t('chrome.app_title', 'AI-First Transformation Partner Assessment');

  useEffect(() => {
    document.title = title ? `${title} — ${appTitle}` : appTitle;
  }, [title, appTitle]);

  useEffect(() => {
    if (focusOnMount && title) {
      headingRef.current?.focus();
    }
    // focusKey re-triggers focus for same-component route changes (question N -> N+1)
  }, [focusOnMount, title, focusKey]);

  return (
    <hgroup>
      <p className={styles.eyebrow}>{appTitle}</p>
      {title && (
        <h1 className={mergeClasses(styles.title, titleClassName)} tabIndex={-1} ref={headingRef}>
          {title}
        </h1>
      )}
    </hgroup>
  );
}
